import React, { useState } from "react";
import * as Mui from "@mui/material";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as api from "api/api";
import { FormField } from "component/shared/form-fields";
import { TextMessageBusinessType, TextMessageTaxIdType } from "type/text-message-tax-id";
import { TaxIdFields, taxIdLabel } from "../tax-id-fields";
import { styles } from "./style";

export interface BusinessTaxIdForm {
	hasTaxId: boolean;
	taxIdType: TextMessageTaxIdType;
	/** What the client typed. Only sent when `taxIdEdited`, since the stored value never leaves the server unasked. */
	taxId: string;
	taxIdEdited: boolean;
	businessType: TextMessageBusinessType | "";
	legalBusinessName: string;
}

export type BusinessTaxIdErrors = Partial<Record<"taxId" | "businessType" | "legalBusinessName", string>>;

interface OwnProps {
	form: BusinessTaxIdForm;
	/** The server's masked value, e.g. XX-XXX6789; null when none is stored. */
	storedMasked?: string | null;
	errors: BusinessTaxIdErrors;
	onChange: (patch: Partial<BusinessTaxIdForm>) => void;
	/** Yes → No; the page confirms before clearing. */
	onAnswerNo: () => void;
	onAnswerYes: () => void;
}

interface Props extends OwnProps, WithStyles<typeof styles> {}

/**
 * The Texting page's Business Tax ID section. The stored tax ID shows masked; Show fetches the full
 * number from the reveal endpoint, and typing replaces it. Remount (key) to forget a revealed value.
 */
const Component: React.FC<Props> = ({ classes = {}, form, storedMasked, errors, onChange, onAnswerNo, onAnswerYes }) => {
	// A new tax ID is typed in the clear; a stored one starts masked.
	const [visible, setVisible] = useState(!storedMasked);
	const [revealed, setRevealed] = useState<string | null>(null);
	const [revealing, setRevealing] = useState(false);
	const [revealFailed, setRevealFailed] = useState(false);
	const hasStored = !!storedMasked && !form.taxIdEdited;

	const toggleVisible = async () => {
		if (visible) {
			setVisible(false);
			return;
		}
		if (hasStored && revealed === null) {
			setRevealing(true);
			setRevealFailed(false);
			try {
				const response = await api.getTextingTaxId();
				setRevealed(response.taxId ?? "");
			} catch{
				setRevealFailed(true);
				setRevealing(false);
				return;
			}
			setRevealing(false);
		}
		setVisible(true);
	};

	let value = form.taxId;
	if (hasStored) {
		value = visible && revealed !== null ? revealed : storedMasked ?? "";
	}
	const helperText = errors.taxId ?? (revealFailed ? "We couldn't show your tax ID. Please try again." : undefined);

	const taxIdField = (
		<FormField
			id="texting-tax-id"
			label={taxIdLabel(form.taxIdType)}
			required
			value={value}
			type={!visible && !hasStored ? "password" : "text"}
			onChange={(event) => onChange({ taxId: event.target.value, taxIdEdited: true })}
			error={!!errors.taxId || revealFailed}
			helperText={helperText}
			endAdornment={
				<Mui.IconButton
					onClick={toggleVisible}
					disabled={revealing}
					aria-label={visible ? "Hide tax ID" : "Show tax ID"}
					className={classes.reveal}
				>
					{visible ? <Icons.Eye size={24} /> : <Icons.EyeOff size={24} />}
				</Mui.IconButton>
			}
		/>
	);

	return (
		<div className={classes.root}>
			<Mui.Typography className={classes.heading}>Business Tax ID</Mui.Typography>
			<div className={classes.content}>
				<Mui.Typography className={classes.text}>
					Does your business have a tax ID? If you aren’t sure, check your IRS CP 575 letter or a W-9 you’ve filled out, or ask your broker or accountant.
				</Mui.Typography>
				<TaxIdFields
					hasTaxId={form.hasTaxId}
					onHasTaxIdChange={(hasTaxId) => (hasTaxId ? onAnswerYes() : onAnswerNo())}
					complianceNote="This must match your official registration for messaging compliance."
					taxIdType={form.taxIdType}
					onTaxIdTypeChange={(taxIdType) => onChange({ taxIdType })}
					taxIdField={
						// While the value is masked, a click selects it all so typing replaces it.
						<div onFocus={(event) => hasStored && !visible && (event.target as HTMLInputElement).select?.()}>{taxIdField}</div>
					}
					businessType={form.businessType}
					onBusinessTypeChange={(businessType) => onChange({ businessType })}
					businessTypeError={errors.businessType}
				>
					<FormField
						id="texting-legal-name"
						label="Legal Business Name"
						required
						value={form.legalBusinessName}
						onChange={(event) => onChange({ legalBusinessName: event.target.value })}
						error={!!errors.legalBusinessName}
						helperText={errors.legalBusinessName}
					/>
				</TaxIdFields>
			</div>
		</div>
	);
};

export const BusinessTaxId = withStyles(Component, styles);
